import { Component, DestroyRef, inject, Injector, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { CompanyRelationService } from 'src/app/Common/CompanyRelation/company-relation.service';
import { CompanyRelationDTO } from 'src/app/Common/CompanyRelation/company-relation.interface';
import { EmailService } from 'src/app/AdvERP/companies/company-email-templates/company-email-templates.service';
import { CompEmailItem } from 'src/app/AdvERP/companies/company-email-templates/company-email-templates.interface';
import { HistoryService } from 'src/app/notification/history/history.service';
import { NotificationMaster_CreateRequestDTO } from 'src/app/notification/history/history.interface';
import { isValidEmail, splitEmailList } from '../multi-email-input/multi-email-input.component';
import { DocEmailDocument, DocEmailSettings } from './document-email.types';

/** Request rows are stored with this status so the worker never sends them by themselves. */
const REQUEST_STATUS = 'Requested';

interface TemplateOption {
  templateId: number;
  tName: string;
  emailTO: string;
  ccTo: string;
  bccTo: string;
  aSubject: string;
  aBody: string;
  emailConfigId: number;
}

type EmailFields = { to: string; cc: string; bcc: string; subject: string; body: string };

/**
 * Preview / edit a document email before it is sent (any document type in DOC_EMAIL_PROFILES).
 *
 * No backend change: Confirm & Send saves an email request row through the existing
 * NotificationMaster Create API (NType 'Email', NStatus 'Requested', Remarks = profile.requestKey)
 * and, when the tenant's settings say so, changes the document status. The tenant's
 * notification service reads the request row (To/CC/BCC/Subject/Body) and sends the email
 * with its attachment. Open it through DocumentEmailPreviewService.open().
 */
@Component({
  selector: 'app-document-email-preview',
  templateUrl: './document-email-preview.component.html',
  styleUrls: ['./document-email-preview.component.css']
})
export class DocumentEmailPreviewComponent implements OnInit {
  @Input() settings!: DocEmailSettings;
  @Input() masterId = 0;

  activeModal = inject(NgbActiveModal);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private toastr = inject(ToastrService);
  private User = inject(UserLoginService).User();
  private relationService = inject(CompanyRelationService);
  private templateService = inject(EmailService);
  private historyService = inject(HistoryService);

  isLoading = true;
  isSending = false;
  loadError = '';

  doc: DocEmailDocument | null = null;
  customer: CompanyRelationDTO | null = null;
  templates: TemplateOption[] = [];
  unresolvedTokens: string[] = [];

  form = new FormGroup({
    templateId: new FormControl<number>(0, { nonNullable: true }),
    to: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    cc: new FormControl('', { nonNullable: true }),
    bcc: new FormControl('', { nonNullable: true }),
    subject: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(500)] }),
    body: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  get label(): string {
    return this.settings?.profile.label || 'Document';
  }

  /** True when the current status allows sending (no status rule = always). */
  get canSendFromStatus(): boolean {
    const allowed = this.settings?.allowedStatuses || [];
    return !allowed.length || allowed.includes(this.normalise(this.doc?.status));
  }

  get canSend(): boolean {
    return !!this.doc && this.canSendFromStatus && !this.isLoading && !this.isSending;
  }

  get allowedLabels(): string {
    return (this.settings?.allowedStatuses || []).map(s => this.statusLabel(s))
      .filter((v, i, a) => a.indexOf(v) === i).join(', ');
  }

  get customerEmails(): string[] {
    return splitEmailList(this.customer?.email || '');
  }

  /** Footer note: tenant's own text, otherwise built from the status on send. */
  get note(): string {
    if (this.settings?.note) { return this.settings.note; }
    const status = this.settings?.statusOnSend;
    return status
      ? `On send, the status changes to ${this.statusLabel(status)} and the notification service emails it on its next run.`
      : 'On send, the email is queued and the notification service emails it on its next run.';
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.checkTokens());
    this.load();
  }

  private load(): void {
    this.isLoading = true;
    this.loadError = '';
    const profile = this.settings.profile;

    forkJoin({
      doc: profile.load(this.injector, this.masterId),
      templates: this.templateService.CompanyEmailTemplate_ReadByTenantId(this.scope({ moduleName: 'AdvCRM' }))
        .pipe(catchError(() => of({ items: [] as CompEmailItem[] })))
    }).pipe(
      switchMap(({ doc, templates }) => {
        this.doc = doc;
        this.templates = this.pickTemplates(templates?.items || []);
        if (!doc?.relationId) {
          return of(null);
        }
        return this.relationService.CompanyRelation_readById(this.scope({ relationId: doc.relationId }))
          .pipe(catchError(() => of(null)));
      })
    ).subscribe({
      next: (customer) => {
        this.customer = customer;
        this.isLoading = false;
        this.applyTemplate(this.templates[0]);
      },
      error: () => {
        this.isLoading = false;
        this.loadError = `The ${this.label.toLowerCase()} could not be loaded. Please close and try again.`;
      }
    });
  }

  /** Tenant templates for this document type (name or sub-module contains the keyword), then the built-in default. */
  private pickTemplates(items: CompEmailItem[]): TemplateOption[] {
    const keyword = this.settings.profile.templateKeyword.toLowerCase();
    const list: TemplateOption[] = items
      .filter(t => (t.isActive ?? 1) === 1)
      .filter(t => `${t.subModuleName || ''} ${t.tName || ''}`.toLowerCase().includes(keyword))
      .map(t => ({
        templateId: t.templateId,
        tName: t.tName,
        emailTO: t.emailTO || '',
        ccTo: t.ccTo || '',
        bccTo: t.bccTo || '',
        aSubject: t.aSubject || '',
        aBody: t.aBody || '',
        emailConfigId: t.emailConfigId || 0
      }));
    const builtIn = this.settings.profile.builtInTemplate;
    return [...list, {
      templateId: 0, tName: 'Default', emailTO: '{{CustomerEmail}}', ccTo: '', bccTo: '',
      aSubject: builtIn.subject, aBody: builtIn.body, emailConfigId: 0
    }];
  }

  onTemplateChange(templateId: number): void {
    const template = this.templates.find(t => t.templateId === templateId);
    if (template) {
      this.applyTemplate(template);
    }
  }

  /** Fills To/CC/BCC/Subject/Body from the template with the document's values. */
  private applyTemplate(template: TemplateOption): void {
    const values = this.buildValues();
    const emails = (text: string) => splitEmailList(this.resolve(text, values)).filter(isValidEmail);
    const to = emails(template.emailTO);

    this.form.patchValue({
      templateId: template.templateId,
      to: (to.length ? to : this.customerEmails).join(';'),
      cc: emails(template.ccTo).join(';'),
      bcc: emails(template.bccTo).join(';'),
      subject: this.resolve(template.aSubject, values),
      body: this.resolve(template.aBody, values)
    });
    this.form.markAsPristine();
    this.checkTokens();
  }

  /** Token values: every document field by name (dates formatted), plus customer fields. */
  private buildValues(): Record<string, string> {
    const values: Record<string, string> = {};
    const source = this.doc?.values || {};
    Object.keys(source).forEach(key => {
      const v = source[key];
      if (v === null || v === undefined || typeof v === 'object') { return; }
      const text = String(v);
      // ISO dates (2026-07-14T00:00:00) are shown as 14 Jul 2026
      values[key.toLowerCase()] = /^\d{4}-\d{2}-\d{2}(T|$)/.test(text) ? this.formatDisplayDate(text) : text;
    });
    values['customername'] = this.customer?.cName || '';
    values['clientname'] = this.customer?.cName || '';
    values['customeremail'] = this.customerEmails.join(';');
    return values;
  }

  /** Replaces {{Token}} (case-insensitive); unknown tokens are left as they are. */
  private resolve(text: string, values: Record<string, string>): string {
    return (text || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, name: string) => {
      const value = values[name.toLowerCase()];
      return value !== undefined ? value : match;
    });
  }

  checkTokens(): void {
    const { to, cc, bcc, subject, body } = this.form.getRawValue();
    const found = `${to} ${cc} ${bcc} ${subject} ${body}`.match(/\{\{\s*[\w.]+\s*\}\}/g) || [];
    this.unresolvedTokens = Array.from(new Set(found));
  }

  send(): void {
    this.checkTokens();
    this.form.markAllAsTouched();
    const v = this.form.getRawValue();

    const error = this.validate(v);
    if (error) {
      this.toastr.warning(error);
      return;
    }

    this.isSending = true;
    const profile = this.settings.profile;
    const statusOnSend = this.settings.statusOnSend;

    // Re-read the document so two users (or two tabs) cannot both send it.
    profile.load(this.injector, this.masterId).pipe(
      switchMap(latest => {
        this.doc = latest;
        if (!this.canSendFromStatus) {
          throw new Error('STATUS_CHANGED');
        }
        // 1. Request row first, so it already exists when the service runs.
        return this.historyService.notificationMaster_Create(this.buildRequest(v));
      }),
      // 2. Then the status change, when this tenant's trigger needs one.
      switchMap((): Observable<unknown> => statusOnSend
        ? profile.setStatus(this.injector, this.masterId, statusOnSend, this.User.userId.toString())
        : of(null))
    ).subscribe({
      next: () => {
        this.isSending = false;
        this.toastr.success(
          statusOnSend
            ? `The ${this.label.toLowerCase()} is marked ${this.statusLabel(statusOnSend)}. The email goes out on the next notification run.`
            : 'The email goes out on the next notification run.',
          'Email queued');
        this.activeModal.close('sent');
      },
      error: (err) => {
        this.isSending = false;
        if (err?.message === 'STATUS_CHANGED') {
          this.toastr.warning(`This ${this.label.toLowerCase()} is now ${this.statusLabel(this.doc?.status)}, so the email was not sent.`);
        } else {
          this.toastr.error('The email could not be queued. Please try again.');
        }
      }
    });
  }

  private validate(v: EmailFields): string {
    const to = splitEmailList(v.to);
    const all = [...to, ...splitEmailList(v.cc), ...splitEmailList(v.bcc)];
    if (!to.length) { return 'Add at least one "To" email address.'; }
    const invalid = all.filter(e => !isValidEmail(e));
    if (invalid.length) { return `Not a valid email: ${invalid.join(', ')}`; }
    if (v.to.length > 1000) { return 'The "To" list is too long (1000 characters at most).'; }
    if (!v.subject.trim()) { return 'Enter a subject.'; }
    if (v.subject.length > 500) { return 'The subject is too long (500 characters at most).'; }
    if (!this.stripHtml(v.body)) { return 'The email body is empty.'; }
    if (this.unresolvedTokens.length) {
      return `Replace or remove these placeholders first: ${this.unresolvedTokens.join(', ')}`;
    }
    return '';
  }

  private buildRequest(v: EmailFields): NotificationMaster_CreateRequestDTO {
    const template = this.templates.find(t => t.templateId === this.form.controls.templateId.value);
    return {
      alertConfigId: template?.emailConfigId || 0,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      softwareId: 1,
      nType: 'Email',
      nSubject: v.subject.trim(),
      nContent: v.body,
      nTo: splitEmailList(v.to).join(';'),
      nCc: splitEmailList(v.cc).join(';'),
      nBcc: splitEmailList(v.bcc).join(';'),
      nStatus: REQUEST_STATUS,
      remarks: this.settings.profile.requestKey(this.masterId),
      scheduledDate: new Date().toISOString(),
      attachmentPath: '',
      retryCount: 0,
      retryDate: null,
      postSendDataSourceType: '',
      postSendDataSourceDef: '',
      dbConnId: 0,
      actionUser: this.User.userId.toString()
    };
  }

  statusLabel(status: string | null | undefined): string {
    const code = this.normalise(status);
    return this.settings?.profile.statusLabels[code] || status || '-';
  }

  private normalise(status: string | null | undefined): string {
    return (status || '').trim().toUpperCase();
  }

  private formatDisplayDate(value: string): string {
    try {
      return formatDate(value, 'dd MMM yyyy', 'en-US');
    } catch {
      return value;
    }
  }

  private stripHtml(html: string): string {
    return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  }

  private scope<T extends object>(data: T): T & { tenantId: number; tenantBranchId: number } {
    return { ...data, tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId };
  }
}
